import { LocalizedString, Money, Price, ProductDiscountValue } from '@commercetools/platform-sdk';

import { PriceFragment } from '@/graphql/fragments/price';

/**
 * ProductVariantSimplifierPrice parallels the CT {@link Price} interface except with
 * certain fields omitted and `obj` always populated. Don't add anything here
 * which isn't present in CT's {@link Price}.
 *
 * This is produced by webstore's
 * `Nuts\Commercetools\Helpers\ProductVariantSimplifier`.
 */
export interface ProductVariantSimplifierPrice {
  value: Money;
  channel?: {
    obj: {
      key: string;
      name?: LocalizedString;
    };
  };
  discounted?: {
    value: Money;
    discount: {
      obj?: {
        name: LocalizedString;
        description?: LocalizedString;
        value: ProductDiscountValue;
      };
    };
  };
  tiers?: {
    minimumQuantity: number;
    value: Money;
  }[];
}

export type PriceLike = Price | PriceFragment | ProductVariantSimplifierPrice;

export const DEFAULT_CHANNEL_KEY = undefined;
export const DEFAULT_SUBSCRIPTION_CHANNEL_KEY = 'autoDelivery5Off';

export function getListPrice<T extends PriceLike>(prices: T[]): T {
  return getPriceByChannelKey(prices, DEFAULT_CHANNEL_KEY);
}

export function getMoneyValue<T extends PriceLike>(price: T, quantity?: number) {
  let moneyValue = price.value;

  if (price.discounted) {
    moneyValue = price.discounted.value;
  } else if (price.tiers) {
    const tier = getPriceTier(price, quantity);
    moneyValue = tier.value;
  }

  return moneyValue;
}

export function getPriceByChannelKey<T extends PriceLike>(prices: T[], channelKey?: string): T {
  const price = prices.find((p) => {
    if (!p.channel) return channelKey === undefined;
    if ('obj' in p.channel || 'typeId' in p.channel) return p.channel.obj?.key === channelKey;
    return p.channel.key === channelKey;
  });

  if (!price) {
    throw new Error(`Cannot find price for channel key: ${channelKey}`);
  }

  return price;
}

export function getPriceTier<T extends PriceLike>(price: T, quantity?: number) {
  const absoluteQuantity = Math.max(quantity ?? 1, 1);
  const tiers = [{ minimumQuantity: 1, value: price.value }, ...(price.tiers ?? [])];
  const tier = tiers.filter((t) => t.minimumQuantity <= absoluteQuantity).pop();

  if (!tier) {
    throw new Error(`Cannot find price tier for quantity: ${quantity}`);
  }

  return tier;
}
